import { useCallback, useRef, useState } from 'react';
import type { TrackedOrder } from '@xshop/shared';
import { lookupOrder } from '../../api/orderLookup';
import { StorefrontApiError } from '../../api/client';

/** Why a lookup found nothing: no such order, too many tries, or the shop could not be asked. */
export type LookupFailure = 'missing' | 'failed' | 'tooMany';

/** Where one lookup stands: not asked yet, asking, found, or why it found nothing. */
export type LookupState = { status: 'idle' } | { status: 'busy' } | { status: 'found'; order: TrackedOrder } | { status: LookupFailure };

// Names why a lookup found nothing from how the shop refused it; a refused shape is a miss like any other.
export function lookupFailure(error: unknown): LookupFailure {
  const status = error instanceof StorefrontApiError ? error.status : undefined;
  if (status === 429) return 'tooMany';
  return status === 404 || status === 400 ? 'missing' : 'failed';
}

// Looks up one order at a time, a later lookup overtaking an answer still on its way.
export function useOrderLookup() {
  const [state, setState] = useState<LookupState>({ status: 'idle' });
  const ticket = useRef(0);

  // Asks for the order under this number and phone, keeping only the newest answer.
  const find = useCallback(async (orderNumber: string, phone: string) => {
    const mine = ++ticket.current;
    setState({ status: 'busy' });
    try {
      const order = await lookupOrder({ orderNumber, phone });
      if (mine === ticket.current) setState({ status: 'found', order });
    } catch (error) {
      if (mine === ticket.current) setState({ status: lookupFailure(error) });
    }
  }, []);

  // Goes back to the empty form, dropping any answer still on its way.
  const reset = useCallback(() => { ticket.current += 1; setState({ status: 'idle' }); }, []);

  return { state, find, reset };
}
